import { Link, useNavigate } from "react-router";
import type { AdminSessionRow } from "../../lib/admin/types";
import { toDate } from "../../lib/admin/types";
import { formatDuration, formatMode, formatPercentage } from "../../lib/admin/formatters";
import { RelativeTime } from "../admin/RelativeTime";

/**
 * `linkContext` controls the back-nav signal we tag onto the report URL,
 * so /report/[sessionId] knows what "back" should mean. "self" is the
 * default (own report from the home page) — no param, no back link.
 * "history" is the /geschiedenis page (the full student session list).
 */
export type LinkContext = "self" | "history" | "admin-user" | "admin-users";

export type SessionsTableProps = {
  sessions: AdminSessionRow[];
  linkContext?: LinkContext;
  emptyCopy?: string;
};

function reportHref(sessionId: string, ctx: LinkContext): string {
  if (ctx === "self") return `/report/${sessionId}`;
  return `/report/${sessionId}?from=${ctx}`;
}

export function SessionsTable({
  sessions,
  linkContext = "self",
  emptyCopy = "Nog geen sessies.",
}: SessionsTableProps) {
  const nav = useNavigate();
  if (sessions.length === 0) {
    return (
      <p className="rounded-xl border border-[var(--border)] bg-[var(--panel)] p-6 text-center text-[var(--ink-muted)]">
        {emptyCopy}
      </p>
    );
  }
  return (
    <div className="rounded-xl border border-[var(--border)] bg-[var(--panel)] overflow-x-auto">
      <table className="w-full text-sm min-w-[640px]">
        <caption className="sr-only">Sessies — klik op een rij voor het rapport</caption>
        <thead className="bg-[var(--panel-soft)] text-[var(--ink-muted)]">
          <tr>
            <th className="text-left font-medium px-4 py-3">Begonnen</th>
            <th className="text-left font-medium px-4 py-3">Modus</th>
            <th className="text-left font-medium px-4 py-3">Duur</th>
            <th className="text-right font-medium px-4 py-3">Score</th>
            <th className="text-right font-medium px-4 py-3">%</th>
            <th className="text-left font-medium px-4 py-3">Resultaat</th>
          </tr>
        </thead>
        <tbody>
          {sessions.map((s, i) => {
            const score =
              s.totalScore !== null && s.maxScore !== null
                ? `${s.totalScore}/${s.maxScore}`
                : "—";
            const result =
              s.passed === null ? (
                <span className="text-[var(--ink-faint)]">—</span>
              ) : s.passed ? (
                <span className="text-[var(--pass)]">Geslaagd</span>
              ) : (
                <span className="text-[var(--fail)]">Niet gehaald</span>
              );
            const href = reportHref(s.id, linkContext);
            const startedAt = toDate(s.startedAt);
            const finishedAt = toDate(s.finishedAt);
            const label = `Bekijk rapport van ${formatMode(s.mode)} op ${
              startedAt ? startedAt.toLocaleDateString("nl-NL") : "?"
            }`;
            return (
              <tr
                key={s.id}
                onClick={() => nav(href)}
                className={
                  "cursor-pointer transition-colors hover:bg-[var(--panel-soft)] " +
                  (i > 0 ? "border-t border-[var(--border)]" : "")
                }
              >
                <td className="px-4 py-3 text-[var(--ink-muted)]">
                  <Link
                    to={href}
                    aria-label={label}
                    className="block focus-visible:text-[var(--accent)]"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <RelativeTime when={startedAt} />
                  </Link>
                </td>
                <td className="px-4 py-3">{formatMode(s.mode)}</td>
                <td className="px-4 py-3 font-mono-badge text-xs">
                  {startedAt ? formatDuration(startedAt, finishedAt) : "—"}
                </td>
                <td className="px-4 py-3 text-right font-mono-badge">{score}</td>
                <td className="px-4 py-3 text-right font-mono-badge">
                  {formatPercentage(s.percentage)}
                </td>
                <td className="px-4 py-3">{result}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
